import cx from "classnames";

import RecordFieldsTable from "../../Composites/RecordFieldsTable/RecordFieldsTable";
import { appstr } from "@coreapp/utils/appstr";
import { BaseballCardHolders } from "./BaseballCardHolders";
import { Button, Divider } from "@theorgchartdev/storybook";
import type { QXDoc } from "@coreapp/CoreApp/QXDoc";
import { PositionHeadingProfileCard } from "./Headers/PositionHeadingProfileCard";
import { getTabFieldData, ProfileTypes } from "./ProfileBaseballCard.utils";
import { getMainDisplayFieldIds } from "@/Utils/getMainDisplayFieldIds";
import { getDisplayMap } from "./getDisplayMap";
import { QObjDBObject } from "@coreapp/CoreApp/QChange";
import { FavoriteManagerInterface } from "./EmployeeBaseballCard";
import {
  HeadingConfigType,
  getDefaultPositionFieldConfig
} from "../../Modals/SettingsModal/ChartSettings/ProfileConfiguration/ProfileConfiguration.utils";

export type PositionCardProps = {
  record: Record<string, any>;
  showMoreFields: boolean | null;
  setShowMoreFields: React.Dispatch<React.SetStateAction<boolean | null>>;
  headingConfig: HeadingConfigType;
  visibleFields: string[];
  employeeCardProps?: {
    favoriteController: FavoriteManagerInterface;
    visibleFields: string[];
  };

  doc?: QXDoc;
  nAssignees?: number;
  reportsToLabel?: string;
  dataObj?: Nullish<QObjDBObject>;
  mapping: Record<string, string>;
};

export const PositionBaseballCard = (props: PositionCardProps) => {
  if (!props.visibleFields) {
    return null;
  }

  const { dept: deptFieldId, ...mainFieldIds } = getMainDisplayFieldIds(true, "Position");
  const upperDisplayMap: Record<string, string> = props.mapping
    ? {
        name: props.mapping[mainFieldIds.name] || mainFieldIds.name,
        id: props.mapping[mainFieldIds.id] || mainFieldIds.id,
        headcount: props.mapping[mainFieldIds.headcount] || mainFieldIds.headcount
      }
    : getDisplayMap(mainFieldIds, "Position", false);

  if (props.mapping[deptFieldId]) {
    upperDisplayMap.dept = props.mapping[deptFieldId];
  }

  const nAssignees = props.nAssignees || 0;

  const baseClass = "ocn-profile-card";
  const deptText = upperDisplayMap.dept ? props.record[upperDisplayMap.dept] : undefined;
  const basicFieldData = {
    header1Text: props.record[upperDisplayMap.name],
    showHeader1: upperDisplayMap.hasOwnProperty("name"),
    subheader1Text: props.record[upperDisplayMap.id],
    showSubheader1: upperDisplayMap.hasOwnProperty("id"),
    header2Text: deptText,
    showHeader2: !!deptText,
    headcount: props.record[upperDisplayMap.headcount],
    showAssignees: nAssignees > 0,
    assignees: nAssignees.toString(),
    reportsToLabel: props.reportsToLabel
  };

  // Exclude fields already shown in the heading
  const showMoreFields = props.visibleFields.filter(fieldId => {
    return !Object.values(upperDisplayMap).includes(fieldId);
  });
  const tabFieldData = getTabFieldData(showMoreFields, props.record, ProfileTypes.Position);

  return (
    <>
      <PositionHeadingProfileCard
        config={
          props.headingConfig?.[ProfileTypes.Position] ??
          getDefaultPositionFieldConfig(props.mapping)
        }
        record={props.record}
        {...basicFieldData}
      />
      <div className={cx(`${baseClass}-content`, "ocn-list-scrollbar")}>
        {tabFieldData.length !== 0 && (
          <>
            {props.showMoreFields && (
              <>
                <RecordFieldsTable className={baseClass} data={tabFieldData} sortEnabled={false} />
              </>
            )}
            <div className={`${baseClass}-additional`}>
              <Button
                dataTestId="profile-card-more-toggle"
                onClick={() => props.setShowMoreFields(prevShowMoreFields => !prevShowMoreFields)}
                size="small"
                modifier="tertiary"
                iconProps={{
                  name: props.showMoreFields ? "square-minus" : "square-plus",
                  size: "small"
                }}
              >
                {props.showMoreFields ? appstr("Show less") : appstr("Show more")}
              </Button>
            </div>
            <Divider />
          </>
        )}
        {props.doc && props.employeeCardProps && (
          <BaseballCardHolders
            doc={props.doc}
            mapping={props.mapping}
            dataObj={props.dataObj}
            record={props.record}
            setShowMoreFields={props.setShowMoreFields}
            headingConfig={props.headingConfig}
            employeeCardProps={props.employeeCardProps}
          />
        )}
      </div>
    </>
  );
};
